import { post } from '@si/utils';
import { useEffect, useState } from 'react';

import { keycloak } from '../keycloak';

interface Params {
  authenticated: boolean;
  isAuthenticating: boolean;
  token?: string;
}

const BOOTSTRAP_SESSION_KEY_PREFIX = 'onboarding-bootstrap-complete:';
const BOOTSTRAP_TIMEOUT_MS = 5000;
const inFlightBootstraps = new Map<string, Promise<void>>();
const releasedSessionKeys = new Set<string>();

export function useOnboardingBootstrap({ authenticated, isAuthenticating, token }: Params) {
  const [isBootstrapping, setIsBootstrapping] = useState(false);

  useEffect(() => {
    if (isAuthenticating || !authenticated || !token) {
      setIsBootstrapping(false);
      return;
    }

    const authIdentity =
      keycloak.subject ??
      (typeof keycloak.tokenParsed?.sub === 'string' ? keycloak.tokenParsed.sub : null) ??
      (typeof keycloak.tokenParsed?.preferred_username === 'string'
        ? keycloak.tokenParsed.preferred_username
        : null) ??
      (typeof keycloak.tokenParsed?.email === 'string' ? keycloak.tokenParsed.email : null) ??
      token;
    const sessionKey = `${BOOTSTRAP_SESSION_KEY_PREFIX}${authIdentity}`;

    if (sessionStorage.getItem(sessionKey) === 'done' || releasedSessionKeys.has(sessionKey)) {
      setIsBootstrapping(false);
      return;
    }

    let cancelled = false;
    setIsBootstrapping(true);

    const bootstrapPromise =
      inFlightBootstraps.get(sessionKey) ??
      post({ url: '/users/current/onboarding/complete' })
        .then(() => {
          sessionStorage.setItem(sessionKey, 'done');
          releasedSessionKeys.delete(sessionKey);
        })
        .catch((error: unknown) => {
          releasedSessionKeys.add(sessionKey);
          // eslint-disable-next-line no-console
          console.error('Onboarding bootstrap failed', error);
        })
        .finally(() => {
          inFlightBootstraps.delete(sessionKey);
        });

    inFlightBootstraps.set(sessionKey, bootstrapPromise);

    const timeoutId = window.setTimeout(() => {
      releasedSessionKeys.add(sessionKey);
      // eslint-disable-next-line no-console
      console.warn(
        `Onboarding bootstrap timed out after ${BOOTSTRAP_TIMEOUT_MS}ms; continuing app load`
      );
      if (!cancelled) {
        setIsBootstrapping(false);
      }
    }, BOOTSTRAP_TIMEOUT_MS);

    bootstrapPromise.finally(() => {
      window.clearTimeout(timeoutId);
      if (!cancelled) {
        setIsBootstrapping(false);
      }
    });

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [authenticated, isAuthenticating, token]);

  return { isBootstrapping };
}
